import { memo, useCallback, useMemo } from "react";
import { useDrag } from "react-dnd";
import GroupListItem from "../../common/fields/GroupListItem.tsx";
import VirtualizedFieldList from "../../common/fields/VirtualizedFieldList.tsx";
import AddToChip from "../addTo/AddToChip.tsx";
import type { ChartOfAccountsCategoryGroup, StatementChartAccount } from "../utils/statementChartOfAccounts.ts";
import type { StatementAccountCategory } from "../utils/statementRowsArea.ts";
import { STATEMENT_ACCOUNT_CATEGORY_ITEM_TYPE } from "../utils/statementRowsArea.ts";
import ChartOfAccountsRow from "./ChartOfAccountsRow.tsx";

interface AccountListItem {
  name: string;
  account: StatementChartAccount;
}

interface Props {
  group: ChartOfAccountsCategoryGroup<StatementChartAccount>;
  defaultExpanded: boolean;
  disabled: boolean;
  onDragStart: () => void;
}

const ChartOfAccountsCategory = ({ group, defaultExpanded, disabled, onDragStart }: Props) => {
  const category = useMemo<StatementAccountCategory>(
    () => ({ caption: group.caption, accounts: group.accounts }),
    [group.caption, group.accounts]
  );
  const listItems = useMemo<AccountListItem[]>(
    () => group.accounts.map((account) => ({ name: `${account.ledger}:${account.accountNo}`, account })),
    [group.accounts]
  );
  const renderAccount = useCallback(
    ({ account }: AccountListItem) => (
      <ChartOfAccountsRow account={account} disabled={disabled} onDragStart={onDragStart} />
    ),
    [disabled, onDragStart]
  );
  const [, drag] = useDrag(
    () => ({
      type: STATEMENT_ACCOUNT_CATEGORY_ITEM_TYPE,
      item: () => {
        onDragStart();
        return category;
      },
      canDrag: () => !disabled,
    }),
    [category, disabled, onDragStart]
  );

  return (
    <GroupListItem
      caption={`${group.caption} (${group.accounts.length})`}
      defaultExpanded={defaultExpanded}
      dragRef={(element) => {
        drag(element);
      }}
      showDragHandle={!disabled}
      actions={disabled ? undefined : <AddToChip source={{ kind: "accountCategory", category }} />}
    >
      <VirtualizedFieldList fields={listItems} groupCaption={group.caption} visible renderField={renderAccount} />
    </GroupListItem>
  );
};

export default memo(ChartOfAccountsCategory);
